<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-flex tw-justify-between tw-items-center">
        <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin">Businesses</h1>
        <base-button @click="add" class="tw-hidden sm:tw-flex">
          <template v-slot:icon>
            <plus-icon />
          </template>
          New Business
        </base-button>
        <base-icon @click="add" class="tw-inline sm:tw-hidden">
          <plus-icon class="tw-h-5 tw-w-5" />
        </base-icon>
      </base-content>
    </div>
    <base-content>
      <base-text-field v-model="query" class="w-full" placeholder="Search businesses" size="md">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <div class="tw-mt-5 tw-flex tw-justify-end">
        <div class="tw-text-sm tw-mr-2 tw-my-auto tw-text-gray-700 tw-cursor-pointer" @click="toggleShowDeleted">Show deleted</div>
        <base-toggle :value="showDeleted" @input="toggleShowDeleted"></base-toggle>
      </div>
      <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-2">
        <template #image="{item: business}">
          <div class="tw-h-12 tw-w-12">
            <Avatar :businessId="business.id" avatarType="business" />
          </div>
        </template>
        <template #name="{item: business}">
          <div class="tw-flex tw-items-center">
            <div>
              <div class="tw-flex tw-items-center">
                <div class="tw-text-base">{{ business.name }}</div>
                <base-badge class="tw-ml-2" v-if="business.isDeleted" color="secondary" size="sm">
                  Deleted
                </base-badge>
              </div>
              <div class="tw-text-xs tw-text-gray-500">{{ business.id }}</div>
            </div>
          </div>
        </template>
        <template #actions="{item: business}">
          <div class="tw-flex tw-items-center tw-space-x-2">
            <base-button type="flat" size="sm" @click="manage(business)">
              <template v-slot:icon>
                <tool-icon />
              </template>
              Manage
            </base-button>
            <base-button type="flat" size="sm" @click="assume(business)">
              <template v-slot:icon>
                <play-icon />
              </template>
              Assume
            </base-button>
            <base-button :disabled="business.isDeleted" type="flat" size="sm" @click="del(business)">
              <template v-slot:icon>
                <trash-icon />
              </template>
              Delete
            </base-button>
          </div>
        </template>
        <template #empty-state>
          <div class="tw-flex tw-justify-center tw-py-5">
            No Results
          </div>
        </template>
        <template #loading-row>
          <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-12 tw-w-12 tw-rounded-full" /></td>
          <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-3 tw-w-60" /><base-skeleton-loader class="tw-h-2 tw-w-24 tw-mt-2" /></td>
          <td class="tw-py-3 tw-px-4">
            <div class="tw-flex tw-space-x-2">
              <base-skeleton-loader class="tw-h-8 tw-w-20" v-for="key in 3" :key="`action-loader-cell-${key}`" />
            </div>
          </td>
        </template>
      </base-table>
      <base-pagination v-model="page" class="tw-mt-2" />
    </base-content>
    <BusinessModal ref="businessModal" @save="refresh" />
    <BusinessDeleteConfirmationModal ref="deleteConfirmationModal" @delete="refresh" />
  </div>
</template>

<script>
import Avatar from "@/components/Avatar.vue";
import BusinessModal from "@/views/admin/businesses/components/BusinessModal";
import BusinessDeleteConfirmationModal from "@/views/admin/businesses/components/BusinessDeleteConfirmationModal";
import redirect from "@/mixins/redirect";
import { PlusIcon, ToolIcon, PlayIcon, TrashIcon, SearchIcon } from "@/assets/svgs";
import { mapActions } from "vuex";
import { debounce } from "debounce";

export default {
  mixins: [redirect],
  components: {
    Avatar,
    BusinessModal,
    BusinessDeleteConfirmationModal,
    PlusIcon,
    ToolIcon,
    PlayIcon,
    TrashIcon,
    SearchIcon,
  },
  data() {
    return {
      isLoading: true,
      page: {
        take: 50,
        skip: 0,
        total: 0,
      },
      query: null,
      showDeleted: false,
      fields: [
        { title: "Image", key: "image", thClass: "tw-w-0 tw-text-center" },
        { title: "Name", key: "name" },
        { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" },
      ],
      items: [],
    };
  },
  watch: {
    page: {
      handler() {
        this.loadPage();
      },
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  methods: {
    ...mapActions("organization", ["changeOrganization"]),
    refresh() {
      this.loadPage();
    },
    add() {
      this.$refs.businessModal.show();
    },
    async assume(item) {
      await this.changeOrganization(item.id);
      await this.redirectToHome();
    },
    manage(item) {
      this.$router.push({
        path: "/admin/businesses/" + item.id + "/business/settings",
      });
    },
    del(item) {
      this.$refs.deleteConfirmationModal.show(item);
    },
    toggleShowDeleted() {
      this.showDeleted = !this.showDeleted;
      this.loadPage();
    },
    async loadPage() {
      try {
        this.isLoading = true;
        const res = await this.$axios.get(
          `/businesses?take=${this.page.take}&skip=${this.page.skip}&query=${encodeURIComponent(this.query || "")}&deleted=${encodeURIComponent(this.showDeleted)}`,
        );
        this.items = res.data.items;
        this.page.take = res.data.take;
        this.page.skip = res.data.skip;
        this.page.total = res.data.total;
      } catch (err) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load businesses",
          message: "We had a problem retrieving the list of businesses. Please reload the page to try again.",
          error: err,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
